import React, { FunctionComponent, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import translate from '@i18n'
import useSportEntities from '@hooks/useSportEntities'
import useFollowMyTeam from '@hooks/useFollowMyTeam'
import { SportEntity } from '@utils/entities'
import PrimaryCTAButton from '@components/Buttons/PrimaryCTAButton'
import FollowMyTeamSportEntity from '@components/FollowMyTeam/FollowMyTeamSportEntity'
import Placeholder from './Placeholder'

const StyledList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`

const FollowMyTeamSportCompetitorsList: FunctionComponent = () => {
  const entityType = 'competitor'

  const { getSportEntitiesList, error } = useSportEntities()

  const competitorsListData = getSportEntitiesList({
    onlyEntityType: entityType,
  })

  const { isFollowing, toggleFollow } = useFollowMyTeam({
    entityType,
  })

  const handleToggleFollowing = useCallback(
    (sportEntity: SportEntity) => {
      toggleFollow(sportEntity)
    },
    [toggleFollow]
  )

  const handleReload = useCallback(() => {
    window.location.reload()
  }, [])

  const hasNonEmptyData = !!competitorsListData?.length

  const sportCompetitorsListContent = useMemo(() => {
    if (hasNonEmptyData) {
      return (
        <StyledList>
          {competitorsListData.map((competitor) => (
            <FollowMyTeamSportEntity
              key={competitor.entityId}
              sportEntity={competitor}
              isFollowing={isFollowing(competitor.entityId)}
              onToggleFollowing={handleToggleFollowing}
            />
          ))}
        </StyledList>
      )
    } else {
      return error ? (
        <Placeholder
          iconName="user-robot-xmarks-solid"
          title={translate('network.error.title')}
          text={translate('network.error.text')}>
          {error && (
            <PrimaryCTAButton
              colorVariant="black"
              size="large"
              iconName="arrow-rotate-left"
              onClick={handleReload}>
              {translate('network.error.reload')}
            </PrimaryCTAButton>
          )}
        </Placeholder>
      ) : null
    }
  }, [
    competitorsListData,
    error,
    hasNonEmptyData,
    isFollowing,
    handleToggleFollowing,
    handleReload,
  ])

  return sportCompetitorsListContent
}

export default FollowMyTeamSportCompetitorsList
